import React, { useState, useRef, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Lock,
  UserPlus,
  Heart,
  User,
  LogOut,
  ChevronDown,
  Settings,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ProtectedRoute = ({ children, requireAuth = true }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const { toast } = useToast();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef(null);

  // Debug logs to see what's happening
  useEffect(() => {
    console.log("🔐 Auth State:", { isAuthenticated, user });
    console.log(
      "📦 LocalStorage access_token:",
      localStorage.getItem("access_token")
    );
  }, [isAuthenticated, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogoutClick = () => {
    console.log("🔄 Logout clicked");
    setIsDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    console.log("✅ Confirm logout");

    // Show success toast first
    toast({
      title: "Logged out successfully",
      description: "You have been signed out of your account.",
      duration: 3000,
    });

    // Clear all user-related localStorage items
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("user_first_name");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_roles");
    localStorage.removeItem("geeth365_user");

    // Call logout to update auth context
    logout();
    setShowLogoutConfirm(false);
  };

  const handleCancelLogout = () => {
    console.log("❌ Cancel logout");
    setShowLogoutConfirm(false);
  };

  const UserDropdown = () => (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="flex items-center space-x-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-full px-4 py-2 transition-all duration-200"
        onClick={() => {
          console.log("🎯 Dropdown clicked");
          setIsDropdownOpen(!isDropdownOpen);
        }}
      >
        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
          <User className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm font-medium text-gray-700 max-w-32 truncate">
          {user?.first_name || user?.name || "User"}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 top-12 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 animate-in fade-in-0 zoom-in-95">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user?.first_name || user?.name || "User"}
            </p>
            <p className="text-xs text-gray-500 mt-1 truncate">
              {user?.email || "Welcome to GEETH365"}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              onClick={() => {
                setIsDropdownOpen(false);
                window.location.href = "/profile";
              }}
            >
              <Settings className="w-4 h-4 mr-3 text-gray-400" />
              Account Settings
            </button>
          </div>

          {/* Logout Section */}
          <div className="border-t border-gray-100 pt-1">
            <button
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
              onClick={handleLogoutClick}
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // If authentication is required and user is not authenticated, show login prompt
  if (requireAuth && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <Card className="border-orange-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Login Required
              </h2>
              <p className="text-gray-600 mb-6">
                Please sign up or login to access your health vitals and account
                features.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => (window.location.href = "/auth")}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Sign Up / Login
                </Button>
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = "/")}
                  className="w-full border-orange-200 text-orange-600"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Browse Healthcare Services
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Free Benefits After Signup:
                </h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>50% OFF on all lab tests</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Personal health vitals tracking</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Priority healthcare support</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Free home sample collection</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with User Dropdown */}
      {isAuthenticated && (
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo/Brand */}
              <div className="flex items-center">
                <h1 className="text-xl font-bold text-orange-600">GEETH365</h1>
              </div>

              {/* Navigation Links */}
              <nav className="hidden md:flex items-center space-x-8">
                <a
                  href="/"
                  className="text-gray-700 hover:text-orange-600 transition-colors duration-200"
                >
                  Dashboard
                </a>
                <a
                  href="/health-vitals"
                  className="text-gray-700 hover:text-orange-600 transition-colors duration-200"
                >
                  Health Vitals
                </a>
                <a
                  href="/appointments"
                  className="text-gray-700 hover:text-orange-600 transition-colors duration-200"
                >
                  Appointments
                </a>
                <a
                  href="/lab-tests"
                  className="text-gray-700 hover:text-orange-600 transition-colors duration-200"
                >
                  Lab Tests
                </a>
              </nav>

              {/* User Dropdown */}
              <UserDropdown />
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={isAuthenticated ? "py-6" : ""}>{children}</main>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 animate-in fade-in-0 duration-300"
          onClick={handleCancelLogout} // Close when clicking overlay
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking dialog
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <LogOut className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Sign Out
                </h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to sign out?
                </p>
              </div>
            </div>

            <div className="flex space-x-3 justify-end mt-6">
              <Button
                variant="outline"
                onClick={handleCancelLogout}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLogout}
                className="bg-red-600 hover:bg-red-700 text-white transition-colors duration-200"
              >
                Yes, Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtectedRoute;
